import { ImageName } from "@mcleod/images";
import { ActionPermission, Api, HorizontalAlignment, LogManager, ModelRow, PermissionsUtil } from "@mcleod/core";
import {
    Button, ButtonVariant, ClickEvent, Image, Label, Layout, ListItem, MultiButton, Panel, SlideoutDecorator, Snackbar,
    TableRowDisplayEvent
} from "@mcleod/components";
import { CommonDialogs } from "@mcleod/common";
import { FilterPanel, OrderTabView } from "@mcleod/dispatch";
import { ProcessLogViewer } from "@mcleod/general";
import { AutogenLayoutInvoiceReadyToProcessList } from "./autogen/AutogenLayoutInvoiceReadyToProcessList";
import {
    INVOICE_PROCESSING_STEP, InvoiceProcessingPrintPostSlideout, PrintPostData
} from "./InvoiceProcessingPrintPostSlideout";
import { Filter, InvoiceProcessingTableType } from "./InvoiceProcessingTableType";
import { InvoiceProcessingTable, InvoiceProcessingTableListeners, updateFilterCounts, scheduleRefreshWithDelay } from "./InvoiceProcessingUtil";
import { InvoiceProcessingToolPanel } from "./InvoiceProcessingToolPanel";
import { ModelArControl } from "./models/ModelArControl";
import { RowInvoiceProcessingProfile } from "./models/ModelInvoiceProcessingProfile";
import { openManageStops } from "./InvoiceProcessing";

const log = LogManager.getLogger("lme.ar.InvoiceReadyToProcessList");

enum ACTION {
    PRINT_AND_POST = "layoutEditGLReport",
    PRINT = "layoutPrintPreview",
    POST = "layoutGLPost"
}
export class InvoiceReadyToProcessList extends AutogenLayoutInvoiceReadyToProcessList implements InvoiceProcessingTable {
    public filterPanel: FilterPanel;
    public type: InvoiceProcessingTableType;
    private _profileRow: RowInvoiceProcessingProfile;
    private multiButtonPrintAndPost: MultiButton;
    private processLogButton: Button;
    private _selectedMoveId: string = "";
    private _transferToSettlement: boolean = false;
    private _preservedBillingIds: Set<string> = new Set(); // Store billing IDs for slideout restoration

    override async onLoad() {
        this.multiButtonPrintAndPost = new MultiButton({
            dropdownItems: this.createDropdownLabels(),
            color: "primary.reverse",
            backgroundColor: "primary"
        });
        this.multiButtonPrintAndPost.primaryButton.setProps({
            themeKey: "editRowDecorator.primaryButton",
            id: "multiButtonPrintAndPost",
            caption: "Print & Post",
            onClick: () => { this.buttonMultiButtonPrintPostOnClick(ACTION.PRINT_AND_POST) },
        });
        this.tableProcessing.addRightTool(this.createProcessLogButton());
        this.tableProcessing.addRightTool(this.multiButtonPrintAndPost);
        this.multiButtonPrintAndPost.enabled = false;
        const actionDenied = PermissionsUtil.isUserDeniedAction(ActionPermission.ACTION_PRINT_POST_INVOICE);
        this.tableProcessing.addSelectionListener(() => {
            const selectionCount = this.tableProcessing.selectedRows.length;
            log.debug(`🔄 Selection changed: ${selectionCount} rows selected`);

            if (selectionCount > 0 && !actionDenied) {
                this.multiButtonPrintAndPost.enabled = true;
            } else {
                this.multiButtonPrintAndPost.enabled = false;
            }
        });
        const arControl = await new ModelArControl().searchSingle({}, "lme/ar/ARControl");
        if (arControl) {
            this.transferToSettlement = arControl.get("transfer_to_settlement") === "Y";
        }
    }
    private createDropdownLabels(): ListItem[] {
        return [
            new ListItem({ align: HorizontalAlignment.LEFT, text: "Print Invoices", onClick: () => { this.buttonMultiButtonPrintPostOnClick(ACTION.PRINT) } }),
            new ListItem({
                align: HorizontalAlignment.LEFT, text: "Post Invoices", onClick: () => {
                    CommonDialogs.showYesNo("Proceed with posting?", "Post Invoices").then(async response => {
                        if (response) {
                            this.buttonMultiButtonPrintPostOnClick(ACTION.POST)
                        }
                    });
                }
            })
        ];
    }
    tableProcessingOnRowDisplay(event: TableRowDisplayEvent): void {
        const tableRow = event.getTableRow();
        const modelRow = event.getTableRow().data as ModelRow;
        tableRow.contextMenuItems = () => InvoiceProcessingToolPanel.getContextMenuItems(this.layoutInvoiceProcessingToolPanel, tableRow);
        InvoiceProcessingTableListeners.invoiceProcessingOnRowDisplayListener(tableRow, modelRow);
        InvoiceProcessingTableListeners.onRowDisplayOrderCount(tableRow, modelRow, this.tableProcessing);

        const exceptionPanel = tableRow.findComponentById("panelException") as Panel;
        const exceptionIcon = exceptionPanel?.findComponentById("imageException") as Image;
        const exceptionText = exceptionPanel?.findComponentById("labelExceptionText") as Label;

        const printed = modelRow.get("printed") as boolean;
        if (printed) {
            exceptionPanel.visible = true;
            exceptionIcon.name = ImageName.triangleWarningSolid;
            exceptionIcon.color = "primary";
            exceptionText.text = "Invoice has been printed";
            exceptionPanel.tooltip = "Invoice has been printed for this record but not posted to the general ledger.";
        } else {
            exceptionPanel.tooltip = null;
        }
        const labelOrderId = tableRow.findComponentById("labelOrderId") as Label;
        if (labelOrderId) {
            labelOrderId.addUnderlineWhenMouseOver();
        }

        tableRow.addDblClickListener(() => {
            this.selectedMoveId = modelRow.get("movement_id");
        });
    }

    configureTableForType(type: InvoiceProcessingTableType): void {
        this.type = type;
        this.tableProcessing.dataSource.orderBy = this.type.defaultSort;
        this.filterPanel = new FilterPanel(type.secondaryFilters, this.tableProcessing, this.panelSecondaryFilters);
        const originalFilters = this.filterPanel.filters;
        this.filterPanel.updateFilterCounts = () => updateFilterCounts(originalFilters, type, this.filterPanel, this.mainDataSource, false);
        this.layoutInvoiceProcessingToolPanel.addMountListener(() => {
            this.tableProcessing.addMountListener(() => this.layoutInvoiceProcessingToolPanel.initialize(this))
        });
    }

    public get filter(): Filter {
        return this.type.filter;
    }

    buttonMultiButtonPrintPostOnClick(layoutName: string) {
        this.multiButtonPrintAndPost.busy = true;

        const initialSelectionCount = this.tableProcessing.selectedRows?.length || 0;
        log.debug(`🖨️ Starting ${layoutName} operation with ${initialSelectionCount} rows selected`);

        // Preserve billing IDs for restoration after slideout close
        this._preservedBillingIds.clear();
        this.tableProcessing.selectedRows?.forEach(row => {
            const billingId = row.data.get("billing_id");
            if (billingId) {
                this._preservedBillingIds.add(billingId);
            }
        });
        log.debug(`💾 Preserved ${this._preservedBillingIds.size} billing IDs before slideout for: ${layoutName}`);

        const layout = Layout.getLayout("lme/ar/InvoiceProcessingPrintPostSlideout", {
            width: window.innerWidth * .75,
            backgroundColor: "defaultBackground",
            fillHeight: true,
            paddingBottom: 50
        }) as InvoiceProcessingPrintPostSlideout;

        const selected: PrintPostData[] = this.tableProcessing.selectedRows.map((tableRow) => {
            return {
                billing_id: tableRow.data.get("billing_id") as string || "",
                order_id: tableRow.data.get("order_id") as string || "",
                customer_id: tableRow.data.get("customer_id") as string || "",
                is_split_pay: (tableRow.data.get("is_split_pay") as boolean) || false,
                bill_type: tableRow.data.get("bill_type") as string || ""
            }
        });
        layout.selectedList = selected;
        layout.profileRow = this.profileRow;
        layout.showTransferTab = this.transferToSettlement && this.profileRow?.get("enable_settlement_transfer", "N") === "Y";

        log.debug(`🔧 Creating slideout for ${layoutName} with ${selected.length} selected records`);

        new SlideoutDecorator({
            layout,
            title: `Print/Post (${selected.length}) Records`,
            fillVerticalSpace: true,
            addlComponents: [layout.buttonNextStep, layout.buttonOpenPayables],
            doAfterSlideIn: async () => {
                log.debug(`🔧 Slideout opened for ${layoutName}, preserved billing IDs: ${this._preservedBillingIds.size}`);
                await layout.setupFromProfile();
                switch (layoutName) {
                    case ACTION.PRINT:
                        layout.allowedMinIndex = INVOICE_PROCESSING_STEP.PRINT_STEP;
                        layout.allowedMaxIndex = INVOICE_PROCESSING_STEP.PRINT_STEP;
                        layout.stepper2.enforceStepSequence = true;
                        await CommonDialogs.showYesNo("Send selected invoice(s) to imaging?", "Send to Imaging?").then(async response => {
                            const sendToImaging = response ? "Y" : "N";
                            log.debug(`🖨️ About to execute print operation with sendToImaging: ${sendToImaging}`);

                            // Capture selections again just before print operation
                            const preSelectionCount = this.tableProcessing.selectedRows?.length || 0;
                            log.debug(`🔍 Pre-print selection count: ${preSelectionCount}`);

                            await layout.printBills(sendToImaging);
                            log.debug(`✅ Print operation completed`);

                            // Check selection state immediately after print
                            const postSelectionCount = this.tableProcessing.selectedRows?.length || 0;
                            log.debug(`🔍 Post-print selection count: ${postSelectionCount}`);

                            if (postSelectionCount === 0 && this._preservedBillingIds.size > 0) {
                                log.debug(`⚠️ Selections lost immediately after print operation, restoring...`);
                                this.restoreSelections(this._preservedBillingIds);
                            }

                            // Start monitoring for additional selection loss after print completes
                            this.startSelectionMonitoring("after print completion");
                        });
                        break;
                    case ACTION.POST:
                        layout.stepper2.enforceStepSequence = false;
                        layout.allowedMinIndex = INVOICE_PROCESSING_STEP.PRINT_STEP;
                        layout.updateStep(INVOICE_PROCESSING_STEP.PRINT_STEP);//HAS TO HAVE FOCUS BEFORE WE CAN MOVE TO NEXT STEP.
                        layout.allowedMinIndex = INVOICE_PROCESSING_STEP.PRINT_CONFIRM;
                        layout.updateStep(INVOICE_PROCESSING_STEP.PRINT_CONFIRM);//HAS TO HAVE FOCUS BEFORE WE CAN MOVE TO NEXT STEP.
                        layout.allowedMinIndex = INVOICE_PROCESSING_STEP.POST_STEP;
                        layout.updateStep(INVOICE_PROCESSING_STEP.POST_STEP);//HAS TO HAVE FOCUS BEFORE WE CAN MOVE TO NEXT STEP.
                        log.debug(`📋 About to execute post operation`);

                        // Capture selections again just before post operation
                        const prePostSelectionCount = this.tableProcessing.selectedRows?.length || 0;
                        log.debug(`🔍 Pre-post selection count: ${prePostSelectionCount}`);

                        await layout.postInvoices();
                        log.debug(`✅ Post operation completed`);

                        // Check selection state immediately after post
                        const postPostSelectionCount = this.tableProcessing.selectedRows?.length || 0;
                        log.debug(`🔍 Post-post selection count: ${postPostSelectionCount}`);

                        if (postPostSelectionCount === 0 && this._preservedBillingIds.size > 0) {
                            log.debug(`⚠️ Selections lost immediately after post operation, restoring...`);
                            this.restoreSelections(this._preservedBillingIds);
                        }

                        // Start monitoring for additional selection loss after post completes
                        this.startSelectionMonitoring("after post completion");
                        break;
                    default:
                    case ACTION.PRINT_AND_POST:
                        layout.stepper2.enforceStepSequence = true;
                        layout.setButtonNextStepBusy(true);
                        log.debug(`🖨️📋 About to execute print and post operation`);

                        // Capture selections again just before print and post operation
                        const prePrintPostSelectionCount = this.tableProcessing.selectedRows?.length || 0;
                        log.debug(`🔍 Pre-print-and-post selection count: ${prePrintPostSelectionCount}`);

                        await layout.sourceInvoiceProcessing.search({ order_list: selected, report_bill_type: this.profileRow.get("bill_type") || "", bill_status: "R" });
                        layout.setButtonNextStepBusy(false);
                        log.debug(`✅ Print and post operation completed`);

                        // Check selection state immediately after print and post
                        const postPrintPostSelectionCount = this.tableProcessing.selectedRows?.length || 0;
                        log.debug(`🔍 Post-print-and-post selection count: ${postPrintPostSelectionCount}`);

                        if (postPrintPostSelectionCount === 0 && this._preservedBillingIds.size > 0) {
                            log.debug(`⚠️ Selections lost immediately after print and post operation, restoring...`);
                            this.restoreSelections(this._preservedBillingIds);
                        }

                        // Start monitoring for additional selection loss after print and post completes
                        this.startSelectionMonitoring("after print and post completion");
                        break;
                }
            },
            doAfterSlideOut: async () => {
                this.multiButtonPrintAndPost.busy = false;

                log.debug(`🎯 Print/Post slideout manually closed by user. Operation: ${layoutName}`);

                // Log current selection state
                const currentSelectionCount = this.tableProcessing.selectedRows?.length || 0;
                log.debug(`🎯 Final slideout close. Current selection count: ${currentSelectionCount}`);
                log.debug(`🎯 Preserved billing IDs still available: ${this._preservedBillingIds.size}`);

                // If selections were lost by the time user closes slideout, restore them one final time
                if (currentSelectionCount === 0 && this._preservedBillingIds.size > 0) {
                    log.debug(`⚠️ Selections lost during slideout workflow, restoring before final close...`);
                    this.restoreSelections(this._preservedBillingIds);
                }

                // Clear preserved billing IDs as workflow is complete
                setTimeout(() => {
                    log.debug(`🧹 Clearing preserved billing IDs as print/post workflow is complete`);
                    this._preservedBillingIds.clear();
                }, 1000);
            }
        });
    }

    onClickOrderIdCount(event: ClickEvent) {
        if (event.target instanceof Label) {
            const orderId = event.target.boundRow?.get("order_id");
            this.tableProcessing.setFilter(`order_id=${orderId}`);
        }
    }

    /** This is an event handler for the onClick event of labelOrderId. */
    labelOrderIdOnClick(event: ClickEvent) {
        const orderId = (event.target as Label).text.replace("[", "").replace("]", "");
        OrderTabView.navigateTo({ key: orderId, newTab: true });
    }

    public get profileRow(): RowInvoiceProcessingProfile {
        return this._profileRow;
    }
    public set profileRow(value: RowInvoiceProcessingProfile) {
        this._profileRow = value;
    }

    public get transferToSettlement(): boolean {
        return this._transferToSettlement;
    }
    public set transferToSettlement(value: boolean) {
        this._transferToSettlement = value;
    }

    /** This is an event handler for the afterCrudClose event of tableProcessing. */
    async tableProcessingAfterCrudClose(event) {
        const sourceBilling = event.target?.layout?.sourceBilling?.data?.[0];
        const sourceOrders = event.target?.layout?.sourceOrders?.data;

        if (!sourceBilling) {
            log.error("No billing data available in afterCrudClose event");
            return;
        }

        const row = {
            billing_id: sourceBilling.get("id") || "",
            order_id: sourceBilling.get("order_id") || "",
            movement_id: (sourceOrders && sourceOrders.length > 0)
            ? sourceOrders[0].get("curr_movement_id") || this.selectedMoveId
            : this.selectedMoveId
        };
        const selected = [row];
        const holdChanged = event.target.layout.holdReasonChanged;
        let reasonCode, reasonCodeText;
        try {
            reasonCode = sourceBilling.get?.("hold_reason_code") ?? "";
            reasonCodeText = sourceBilling.get?.("hold_reason_text") ?? "";
        } catch (e) {
            log.debug("Failed to get hold reason fields from sourceBilling", e);
            return;
        }

        if ((holdChanged) && (reasonCode)) {
            const result = await Api.post("lme/ar/invoice-on-hold", { billing_id_list: selected, hold_reason_code: reasonCode, hold_reason_text: reasonCodeText, save_billing_row: false });
            if (!result) {
                Snackbar.showWarningSnackbar("Error placing invoice on hold.");
            } else {
                // Trigger data refresh after successful operation
                this.refreshData();
            }

        }
    }

    public get selectedMoveId(): string {
        return this._selectedMoveId;
    }

    public set selectedMoveId(value: string) {
        this._selectedMoveId = value;
    }

    private createProcessLogButton(): Button {
        if (!this.processLogButton) {
            this.processLogButton = new Button({
                variant: ButtonVariant.text,
                caption: `Process Log Viewer`,
                rowBreak: false,
                borderWidth: 1,
                color: "primary",
                imageRotation: 90,
                borderColor: "primary"
            });
            this.processLogButton.addClickListener(() => ProcessLogViewer.showInSlideoutDecorator("Billing Posting"));
        }
        return this.processLogButton;
    }

    /** This is an event handler for the onClick event of labelStopsPickDrop. */
    labelStopsPickDropOnClick(event: ClickEvent) {
        openManageStops(event);
    }

    private refreshData(recordCount?: number) {
        scheduleRefreshWithDelay(() => this.doRefreshData(), recordCount, "lme.ar.InvoiceReadyToProcessList");
    }

    private doRefreshData() {
        log.debug("Starting data refresh after state change");

        if (!this.mainDataSource) {
            console.error("InvoiceReadyToProcessList: No mainDataSource available for refresh");
            return;
        }

        // Try to find the main InvoiceProcessing layout first
        const mainLayout = this.findMainInvoiceProcessingLayout();
        if (mainLayout) {
            log.debug("Found InvoiceProcessing layout, using searchMainDatasource to preserve profiles");
            mainLayout.searchMainDatasource();
            setTimeout(() => this.updateAllFilterCounts(), 200);
            return;
        }

        // Fallback: Use profileRow if available, otherwise use default
        const searchParams = this.profileRow ? { profile_row: this.profileRow } : {};
        const refreshType = this.profileRow ? "profileRow approach" : "generic refresh (will reset to default profile)";

        log.debug(`Using ${refreshType} as fallback`);
        this.executeDataRefresh(searchParams, refreshType);
    }

    /**
     * Finds the main InvoiceProcessing layout by walking up the parent hierarchy
     */
    private findMainInvoiceProcessingLayout(): any {
        let layout = this as any;
        while (layout && layout.parent) {
            layout = layout.parent;
            if (layout.constructor.name === 'InvoiceProcessing' || layout.searchMainDatasource) {
                return layout;
            }
        }
        return null;
    }

    /**
     * Executes the data refresh with the given search parameters
     */
    private executeDataRefresh(searchParams: any, refreshType: string) {
        this.mainDataSource.clear();
        this.mainDataSource.search(searchParams)
            .then(() => {
                log.debug(`${refreshType} completed, updating filter counts`);
                this.updateAllFilterCounts();
            })
            .catch(error => {
                console.error(`Error during ${refreshType}:`, error);
            });
    }

    private updateAllFilterCounts() {
        // Force update of all filter counts to ensure tabs reflect current data
        log.debug("Starting filter count update");

        setTimeout(() => {
            try {
                // Update filter counts for this tab
                if (this.filterPanel && this.filterPanel.updateFilterCounts) {
                    this.filterPanel.updateFilterCounts();
                }

                // Try to update filter counts for sibling tabs
                this.updateSiblingTabCounts();

            } catch (error) {
                log.error("Error updating filter counts:", error);
            }
        }, 100);

        // Do a second pass with a longer delay to catch any delayed updates
        setTimeout(() => {
            try {
                if (this.filterPanel && this.filterPanel.updateFilterCounts) {
                    this.filterPanel.updateFilterCounts();
                }
                this.updateSiblingTabCounts();
            } catch (error) {
                log.error("Error in second pass filter count update:", error);
            }
        }, 500);
    }

    private updateSiblingTabCounts() {
        try {
            let parentLayout = this as any;
            let foundParent = false;

            // Walk up to find the parent that contains our sibling tabs
            while (parentLayout && parentLayout.parent && !foundParent) {
                parentLayout = parentLayout.parent;

                // Look for sibling tab layouts (OnHold, ReadyToProcess, etc.)
                if (parentLayout.children) {
                    let invoiceProcessingTabCount = 0;
                    for (const child of parentLayout.children) {
                        if (child.constructor.name.includes('InvoiceProcessing')) {
                            invoiceProcessingTabCount++;
                        }
                    }

                    // If we found multiple invoice processing tabs, this is likely the right parent
                    if (invoiceProcessingTabCount > 1) {
                        foundParent = true;
                        log.debug(`Found parent with ${invoiceProcessingTabCount} invoice processing tabs`);

                        for (const child of parentLayout.children) {
                            if (child !== this && child.filterPanel && child.filterPanel.updateFilterCounts) {
                                child.filterPanel.updateFilterCounts();
                            }
                        }
                    }
                }
            }

            if (!foundParent) {
                log.debug("Could not find parent with sibling tabs");
            }
        } catch (error) {
            log.debug("Error in updateSiblingTabCounts:", error);
        }
    }



    /**
     * Restores selections based on preserved billing IDs
     */
    private restoreSelections(selectedBillingIds: Set<string>): void {
        if (selectedBillingIds.size === 0) {
            log.debug("⚠️ No preserved billing IDs to restore");
            return;
        }

        log.debug(`🔄 Attempting to restore ${selectedBillingIds.size} preserved billing IDs...`);
        log.debug(`🔄 Available table rows: ${this.tableProcessing.rows?.length || 0}`);

        // Use a very small timeout to ensure table is ready
        setTimeout(() => {
            let restoredCount = 0;
            const availableBillingIds: string[] = [];

            this.tableProcessing.rows.forEach(tableRow => {
                const billingId = tableRow.data?.get("billing_id");
                if (billingId) {
                    availableBillingIds.push(billingId);
                    if (selectedBillingIds.has(billingId)) {
                        tableRow.selected = true;
                        restoredCount++;
                        log.debug(`✅ Restored selection for billing ID: ${billingId}`);
                    }
                }
            });

            log.debug(`✅ Locally restored ${restoredCount}/${selectedBillingIds.size} selections`);

            if (restoredCount === 0) {
                log.debug(`❌ No selections restored! Available billing IDs:`, availableBillingIds);
                log.debug(`❌ Looking for billing IDs:`, Array.from(selectedBillingIds));
            }

            // Verify the restoration worked
            const finalSelectionCount = this.tableProcessing.selectedRows?.length || 0;
            log.debug(`🔍 Final verification: ${finalSelectionCount} rows now selected`);
        }, 10); // Very small delay but enough for DOM updates
    }

    /**
     * Starts monitoring for unexpected selection changes during slideout workflow
     */
    private startSelectionMonitoring(context: string): void {
        log.debug(`👁️ Starting selection monitoring: ${context}`);

        let monitoringCount = 0;
        const maxMonitoringAttempts = 10; // Monitor for 5 seconds (500ms * 10)

        const monitorInterval = setInterval(() => {
            monitoringCount++;
            const currentSelectionCount = this.tableProcessing.selectedRows?.length || 0;

            log.debug(`👁️ Selection monitor (${monitoringCount}): ${currentSelectionCount} rows selected`);

            // If selections are lost during monitoring, restore them immediately
            if (currentSelectionCount === 0 && this._preservedBillingIds.size > 0) {
                log.debug(`🚨 SELECTION LOSS DETECTED during monitoring! Restoring immediately...`);
                this.restoreSelections(this._preservedBillingIds);
            }

            // Stop monitoring after max attempts or if slideout workflow seems complete
            if (monitoringCount >= maxMonitoringAttempts || !this.multiButtonPrintAndPost.busy) {
                log.debug(`👁️ Stopping selection monitoring after ${monitoringCount} checks`);
                clearInterval(monitorInterval);
            }
        }, 500); // Check every 500ms
    }
}
